import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { DEFAULT_TAB_PATH, tabForPath } from './utils/routeManagementHelpers';
import { AuthProvider, useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import RouteManagementPage from './pages/RouteManagementPage';
import CommandCenterLayout from './components/admin/CommandCenterLayout';
import CommandCenterPage from './pages/CommandCenterPage';
import UserActivityPage from './pages/UserActivityPage';
import TeamPage from './pages/admin/TeamPage';
import DataGovernancePage from './pages/admin/DataGovernancePage';
import InsightsPage from './pages/admin/InsightsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import TrashPage from './pages/admin/TrashPage';
import ProfilePage from './pages/admin/ProfilePage';
import RequireSuperAdmin from './components/admin/RequireSuperAdmin';

/*
 * Guards the dynamic workspace segment.
 *
 * `/:tab` matches any single-segment address, so an unknown one (a typo, or a
 * stale bookmark) is sent to the dashboard rather than rendering an empty shell.
 */
function WorkspaceRoute() {
  const { tab } = useParams();
  if (!tabForPath(`/${tab}`)) {
    return <Navigate to={DEFAULT_TAB_PATH} replace />;
  }
  return <RouteManagementPage />;
}

function AppShell() {
  const { status, isAuthenticated } = useAuth();

  if (status === 'initializing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2.5 text-slate-500 text-[13px]">
          <span className="h-3 w-3 rounded-full bg-brand-500 animate-pulse-soft" />
          Loading workspace…
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={DEFAULT_TAB_PATH} replace /> : <LoginPage />}
      />
      {/* The workspace lives at /home, /stops, /map and /routes. */}
      <Route path="/" element={<Navigate to={DEFAULT_TAB_PATH} replace />} />

      {/*
        One route element covers all four tabs on purpose.
        Declaring a separate <Route> per tab would give React a different element
        for each address, remounting the whole workspace — and re-fetching every
        stop and route — on each tab switch. A single dynamic segment keeps the
        same component mounted and lets the URL choose what it renders.
        Static paths such as /login and /command-center rank above a dynamic
        segment in React Router, so they still win.
      */}
      <Route
        path="/:tab"
        element={
          isAuthenticated ? <WorkspaceRoute /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/command-center"
        element={
          <RequireSuperAdmin>
            <CommandCenterLayout />
          </RequireSuperAdmin>
        }
      >
        <Route index element={<CommandCenterPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="users/:userId" element={<UserActivityPage />} />
        <Route path="data" element={<DataGovernancePage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="trash" element={<TrashPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
